/**
 * Member search matching + ranking.
 *
 * One matcher shared by every member picker in the app (the header search in
 * `Layout`, the check-in modal, the membership/PT forms, the activity-log
 * filters). Keeping it here rather than inline in `MemberSearchableDropdown`
 * means the header can pre-filter and rank the same way react-select filters,
 * so the two can never disagree about what "matches".
 *
 * Matching is deliberately substring-based, not prefix-based: front-desk staff
 * type fragments ("an" for Joanna), and a prefix rule would silently drop them.
 */

import type { SearchableMember } from '@/types';

/** Every digit in the string, with `+`, spaces, dashes and parens dropped. */
export function normalizePhone(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(/\D/g, '');
}

/**
 * Phone queries need at least this many digits before they match anything.
 * Below it, a stray "1" would match most of the roster.
 */
const MIN_PHONE_QUERY_DIGITS = 3;

/**
 * Indonesian numbers are written both ways in the roster ("0812…" locally,
 * "+62 812…" internationally), so a search for one has to find the other.
 * Returns the equivalent forms of a digits-only string, itself included.
 */
function phoneVariants(digits: string): string[] {
  if (!digits) return [];

  const variants = [digits];

  // Local → international: 0812… also searches as 62812…
  if (digits.startsWith('0')) {
    variants.push(`62${digits.slice(1)}`);
  }

  // International → local: 62812… also searches as 0812…
  if (digits.startsWith('62')) {
    variants.push(`0${digits.slice(2)}`);
  }

  return variants;
}

/**
 * Only queries shaped like a phone number reach the phone branch. Without this
 * gate, any text carrying three digits ("apt123", "room456") gets stripped down
 * to its digits and matched against everyone's number, which produces hits that
 * look like nonsense to whoever typed it.
 */
const PHONE_LIKE_QUERY = /^[\d+\-().\s]+$/;

/** Lowercased, whitespace-collapsed search tokens. */
function tokenize(query: string): string[] {
  return query.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/** The text fields a token may match, lowercased. */
function textHaystack(member: SearchableMember): string[] {
  const first = (member.first_name ?? '').toLowerCase();
  const last = (member.last_name ?? '').toLowerCase();

  return [
    first,
    last,
    // The joined name lets a single token span the gap ("john sm" arrives as
    // two tokens, but "ohn sm" is one and still has to match something).
    `${first} ${last}`.trim(),
    (member.email ?? '').toLowerCase(),
  ];
}

/**
 * Does this member match the query?
 *
 * Two independent branches — a member matches if either hits:
 *
 * - **Text**: EVERY token must be a substring of at least one of first name,
 *   last name, the joined full name, or email. The per-token AND is what makes
 *   "john sm" find John Smith; the old per-field OR matched neither token's
 *   field and so returned nothing. Token order doesn't matter ("sm john" works).
 * - **Phone**: a phone-shaped query carrying at least three digits is also
 *   compared, digits only, against `whatsapp_number` — with 0 ↔ 62 equivalence
 *   both ways.
 *
 * An empty query matches everything (react-select asks for the unfiltered list
 * when the input is empty).
 */
export function memberMatchesQuery(member: SearchableMember, query: string): boolean {
  const tokens = tokenize(query);
  if (tokens.length === 0) return true;

  const haystack = textHaystack(member);
  const matchesText = tokens.every(token => haystack.some(field => field.includes(token)));
  if (matchesText) return true;

  if (!PHONE_LIKE_QUERY.test(query.trim())) return false;

  const queryDigits = normalizePhone(query);
  if (queryDigits.length < MIN_PHONE_QUERY_DIGITS) return false;

  const memberDigits = normalizePhone(member.whatsapp_number);
  if (!memberDigits) return false;

  return phoneVariants(queryDigits).some(variant => memberDigits.includes(variant));
}

/**
 * Relevance tiers, lower sorts first. Only used where results are capped (the
 * header search shows the top few), because a cap without ranking would hide
 * the obvious hit behind whatever the database happened to return first.
 */
const RANK_NAME_PREFIX = 0;
const RANK_NAME_CONTAINS = 1;
const RANK_EMAIL_PREFIX = 2;
const RANK_EMAIL_CONTAINS = 3;
const RANK_PHONE = 4;
const RANK_NONE = 5;

function relevance(member: SearchableMember, query: string): number {
  const needle = query.trim().toLowerCase();
  if (!needle) return RANK_NONE;

  const first = (member.first_name ?? '').toLowerCase();
  const last = (member.last_name ?? '').toLowerCase();
  const fullName = `${first} ${last}`.trim();
  const email = (member.email ?? '').toLowerCase();

  // A multi-token query ("john sm") won't be a substring of any single field,
  // so fall back to ranking on its first token — the surname fragment still
  // narrowed the result set via memberMatchesQuery.
  const [firstToken = ''] = tokenize(query);

  for (const candidate of [needle, firstToken]) {
    if (!candidate) continue;
    if (first.startsWith(candidate) || last.startsWith(candidate) || fullName.startsWith(candidate)) {
      return RANK_NAME_PREFIX;
    }
    if (fullName.includes(candidate)) {
      return RANK_NAME_CONTAINS;
    }
  }

  if (email.startsWith(needle)) return RANK_EMAIL_PREFIX;
  if (email.includes(needle)) return RANK_EMAIL_CONTAINS;

  return RANK_PHONE;
}

export interface RankedMemberResults<T extends SearchableMember = SearchableMember> {
  /** The best `limit` matches, most relevant first. */
  members: T[];
  /** How many members matched in total, before the cap. */
  totalMatches: number;
}

/**
 * Filter by `memberMatchesQuery`, order by relevance, and cap.
 *
 * `totalMatches` comes back alongside so the caller can tell the user results
 * were truncated — a silently capped list reads as a complete one.
 */
export function rankMembers<T extends SearchableMember>(
  members: T[] | undefined,
  query: string,
  limit: number,
): RankedMemberResults<T> {
  if (!members?.length || !query.trim()) {
    return { members: [], totalMatches: 0 };
  }

  const matches = members.filter(member => memberMatchesQuery(member, query));

  const sorted = matches
    .map(member => ({ member, rank: relevance(member, query) }))
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      const nameA = `${a.member.first_name ?? ''} ${a.member.last_name ?? ''}`.trim();
      const nameB = `${b.member.first_name ?? ''} ${b.member.last_name ?? ''}`.trim();
      return nameA.localeCompare(nameB);
    })
    .map(entry => entry.member);

  return {
    members: sorted.slice(0, limit),
    totalMatches: matches.length,
  };
}
